import { isApiError } from './api.js';

/**
 * Cabinet suspendu. La plateforme peut suspendre un cabinet ; le serveur répond alors
 * `403 organization_suspended` à toute lecture comme à toute écriture. Sans ce magasin, le
 * praticien verrait une pluie de notifications d'erreur sans jamais apprendre ce qui se passe.
 *
 * Écrit en magasin externe et non en état React parce que le refus arrive depuis le cache de
 * requêtes, hors de tout rendu — exactement comme le jeton d'accès.
 *
 * L'état est **levé** au changement de cabinet et à la déconnexion : un praticien membre de deux
 * cabinets, dont un seul est suspendu, doit pouvoir travailler dans l'autre.
 */

export const SUSPENDED_CODE = 'organization_suspended';

export interface Suspension {
  /** Motif écrit par la plateforme au moment de la suspension. Absent, l'écran reste générique. */
  reason: string | null;
}

let suspension: Suspension | null = null;
const listeners = new Set<() => void>();

function publish(next: Suspension | null): void {
  suspension = next;
  for (const listener of listeners) listener();
}

export function subscribeSuspension(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Retourne toujours la **même** référence tant que rien ne change : l'abonnement l'exige. */
export function getSuspension(): Suspension | null {
  return suspension;
}

/** Le motif tel que le serveur le rend. Une forme inattendue ne fait pas tomber l'écran. */
function readReason(details: unknown): string | null {
  if (typeof details !== 'object' || details === null) return null;
  const reason = (details as { reason?: unknown }).reason;
  return typeof reason === 'string' && reason.trim() !== '' ? reason : null;
}

/**
 * Branché sur les deux caches de requêtes. Une erreur qui n'est pas une suspension ne touche à
 * rien : ce magasin ne connaît qu'un seul code.
 */
export function reportApiError(error: unknown): void {
  if (!isApiError(error) || error.code !== SUSPENDED_CODE) return;
  const reason = readReason(error.details);
  // Deux refus identiques ne republient pas : le rendu ne doit pas boucler sur une rafale.
  if (suspension !== null && suspension.reason === reason) return;
  publish({ reason });
}

export function clearSuspension(): void {
  if (suspension === null) return;
  publish(null);
}
